// SecurityManager.tsx - Comprehensive security and permission enforcement
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Alert, Box, Typography, Button } from "@mui/material";
import { Security, Warning } from "@mui/icons-material";
import { useAppContext, User } from "../../../context";

interface SecurityManagerProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requireActive?: boolean;
  requireStaff?: boolean;
  requireSuperuser?: boolean;
  fallbackComponent?: React.ReactNode;
  redirectTo?: string;
  allowAnonymous?: boolean;
}

export const SecurityManager: React.FC<SecurityManagerProps> = ({
  children,
  requireAuth = true,
  requireActive = true,
  requireStaff = false,
  requireSuperuser = false,
  fallbackComponent,
  redirectTo,
  allowAnonymous = false,
}) => {
  const { user } = useAppContext();
  const location = useLocation();
  const isAuthenticated = Boolean(user);

  // Allow anonymous access if specified
  if (allowAnonymous && !isAuthenticated) {
    return <>{children}</>;
  }

  // Check authentication requirement
  if (requireAuth && !isAuthenticated) {
    const loginUrl = `/auth/login?next=${encodeURIComponent(location.pathname)}`;
    return <Navigate to={loginUrl} replace />;
  }

  if (requireAuth && !user) {
    return <Navigate to="/auth/login" replace />;
  }

  const currentUser = user as User;

  // Check if user account is active
  if (requireActive && currentUser && currentUser.isActive === false) {
    if (fallbackComponent) {
      return <>{fallbackComponent}</>;
    }
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" icon={<Warning />}>
          <Typography variant="h6" gutterBottom>
            Account Inactive
          </Typography>
          <Typography variant="body2">
            Your account has been deactivated. Please contact support for
            assistance.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            sx={{ mt: 2 }}
            href="/support/"
          >
            Contact Support
          </Button>
        </Alert>
      </Box>
    );
  }

  // Check staff requirement
  if (
    requireStaff &&
    currentUser &&
    !currentUser.isStaff &&
    !currentUser.isSuperuser
  ) {
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }
    if (fallbackComponent) {
      return <>{fallbackComponent}</>;
    }
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" icon={<Security />}>
          <Typography variant="h6" gutterBottom>
            Staff Access Required
          </Typography>
          <Typography variant="body2">
            This feature requires staff privileges. Contact an administrator if
            you believe this is an error.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            sx={{ mt: 2 }}
            onClick={() => window.history.back()}
          >
            Go Back
          </Button>
        </Alert>
      </Box>
    );
  }

  // Check superuser requirement
  if (requireSuperuser && currentUser && !currentUser.isSuperuser) {
    const unauthorizedRedirect = redirectTo || "/404";
    return <Navigate to={unauthorizedRedirect} replace />;
  }

  // All security checks passed
  return <>{children}</>;
};

// Higher-level security components for common use cases
export const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <SecurityManager requireAuth={true} requireActive={true}>
    {children}
  </SecurityManager>
);

export const StaffOnlyRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <SecurityManager
    requireAuth={true}
    requireActive={true}
    requireStaff={true}
    redirectTo="/404"
  >
    {children}
  </SecurityManager>
);

export const SuperuserOnlyRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <SecurityManager
    requireAuth={true}
    requireActive={true}
    requireSuperuser={true}
    redirectTo="/404"
  >
    {children}
  </SecurityManager>
);

export const AdminRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <SecurityManager
    requireAuth={true}
    requireActive={true}
    requireSuperuser={true}
    redirectTo="/404"
  >
    {children}
  </SecurityManager>
);

// Permission checking hook for components
export const useSecurityCheck = () => {
  const { user } = useAppContext();
  const isAuthenticated = Boolean(user);

  const checkPermission = (
    requirement: "auth" | "active" | "staff" | "superuser",
  ): boolean => {
    if (requirement === "auth") return isAuthenticated;
    if (!user) return false;

    const currentUser = user as User;

    switch (requirement) {
      case "active":
        // If isActive is undefined, assume user is active (since they're logged in)
        return currentUser.isActive !== false;
      case "staff":
        return Boolean(currentUser.isStaff) || Boolean(currentUser.isSuperuser);
      case "superuser":
        return Boolean(currentUser.isSuperuser);
      default:
        return false;
    }
  };

  return {
    isAuthenticated,
    user: user as User | null,
    canAccess: checkPermission,
    isActive: checkPermission("active"),
    isStaff: checkPermission("staff"),
    isSuperuser: checkPermission("superuser"),
  };
};
